/**
 * 邀请/佣金状态存储
 */

import { ref, computed } from 'vue'
import type { IStorage } from '../storage/Storage'
import type { RewardDetail, RecommendUser, RewardItem, WithdrawRecord } from '../../types/invite'
import { STORAGE_KEYS } from '../../constants/storage-keys'

const LOCAL_STORAGE_KEYS = {
  REWARD_DETAIL: 'invite_reward_detail'
}

/** 邀请状态 */
export interface InviteState {
  inviteId: number | null
  rewardDetail: RewardDetail | null
  recommendUsers: RecommendUser[]
  rewardList: RewardItem[]
  withdrawRecords: WithdrawRecord[]
}

export class InviteStore {
  protected storage: IStorage

  // 邀请 ID
  private _inviteId = ref<number | null>(null)

  // 佣金详情
  private _rewardDetail = ref<RewardDetail | null>(null)

  // 推荐用户列表
  private _recommendUsers = ref<RecommendUser[]>([])

  // 佣金记录列表
  private _rewardList = ref<RewardItem[]>([])

  // 提现记录列表
  private _withdrawRecords = ref<WithdrawRecord[]>([])

  constructor(storage: IStorage) {
    this.storage = storage
    this.restore()
  }

  // Getters
  get inviteId() {
    return this._inviteId.value
  }

  set inviteId(value: number | null) {
    this._inviteId.value = value
    if (value) {
      this.storage.setItem(STORAGE_KEYS.INVITE_ID, value)
    }
  }

  get rewardDetail() {
    return this._rewardDetail.value
  }

  set rewardDetail(value: RewardDetail | null) {
    this._rewardDetail.value = value
    if (value) {
      this.storage.setItem(LOCAL_STORAGE_KEYS.REWARD_DETAIL, value)
    }
  }

  get recommendUsers() {
    return this._recommendUsers.value
  }

  set recommendUsers(value: RecommendUser[]) {
    this._recommendUsers.value = value
  }

  get rewardList() {
    return this._rewardList.value
  }

  set rewardList(value: RewardItem[]) {
    this._rewardList.value = value
  }

  get withdrawRecords() {
    return this._withdrawRecords.value
  }

  set withdrawRecords(value: WithdrawRecord[]) {
    this._withdrawRecords.value = value
  }

  // 计算属性
  /** 可用佣金 */
  availableReward = computed(() => this._rewardDetail.value?.available ?? 0)

  /** 冻结佣金 */
  frozenReward = computed(() => this._rewardDetail.value?.freeze ?? 0)

  /** 总佣金 */
  totalReward = computed(() => this._rewardDetail.value?.total ?? 0)

  /** 已提现金额 */
  withdrawnAmount = computed(() => this._rewardDetail.value?.withdrawn ?? 0)

  /** 是否可以提现（可用佣金 > 0） */
  canWithdraw = computed(() => (this._rewardDetail.value?.available ?? 0) > 0)

  /** 推荐用户数量 */
  recommendUserCount = computed(() => this._recommendUsers.value.length)

  /**
   * 恢复存储的数据
   */
  private restore(): void {
    const inviteId = this.storage.getItem<number>(STORAGE_KEYS.INVITE_ID)
    if (inviteId) {
      this._inviteId.value = inviteId
    }

    const rewardDetail = this.storage.getItem<RewardDetail>(LOCAL_STORAGE_KEYS.REWARD_DETAIL)
    if (rewardDetail) {
      this._rewardDetail.value = rewardDetail
    }
  }

  /**
   * 清除所有数据
   */
  clear(): void {
    this._inviteId.value = null
    this._rewardDetail.value = null
    this._recommendUsers.value = []
    this._rewardList.value = []
    this._withdrawRecords.value = []
    this.storage.removeItem(STORAGE_KEYS.INVITE_ID)
    this.storage.removeItem(LOCAL_STORAGE_KEYS.REWARD_DETAIL)
  }

  /**
   * 获取当前状态
   */
  getState(): InviteState {
    return {
      inviteId: this._inviteId.value,
      rewardDetail: this._rewardDetail.value,
      recommendUsers: this._recommendUsers.value,
      rewardList: this._rewardList.value,
      withdrawRecords: this._withdrawRecords.value
    }
  }
}
